import { useState, type FormEvent, type ReactElement } from 'react';

import { INLINE_LINK, Cta, fieldClasses } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';
import { formatLocaleNumber } from '@/i18n/format';

/**
 * B25 — the /contact lead-capture form (operator decision 2026-06-11: email
 * form + published aliases; NO booking link).
 *
 * Posts to the lane-C `POST /v1/contact` contract (Turnstile-gated, AF-328
 * per-IP throttled, Zod-validated — board handoff 2026-06-12). Mirrors the
 * The API origin arrives at build time from `VITE_SYNCANIX_API_URL`, so it
 * time; native form semantics; visible labels; status/alert live regions.
 *
 * **Graceful degradation is a first-class path, not an apology**: until the
 * endpoint (and the marketing-side Turnstile sitekey wiring) is live, every
 * submit resolves to the fallback panel that surfaces the published
 * `sales@` / `support@` / `security@` addresses with a prefilled mailto —
 * the operator-approved alternative channel. The same panel is the permanent
 * error path for network failures and throttling once the endpoint exists.
 *
 * **Honeypot** (spam negative-path): the visually-hidden `website` field is
 * `aria-hidden` + `tabIndex={-1}` so humans never see or reach it; a filled
 * value short-circuits to the success panel WITHOUT any network call, so
 * naive bots learn nothing and cost nothing.
 */

import { CONTACT_ADDRESSES, type ContactTopic } from './contact-addresses';

const TOPICS: readonly { value: ContactTopic; labelKey: string }[] = [
  { value: 'sales', labelKey: 'contact.form.topic.sales' },
  { value: 'support', labelKey: 'contact.form.topic.support' },
  { value: 'security', labelKey: 'contact.form.topic.security' },
  { value: 'press', labelKey: 'contact.form.topic.press' },
];

const MESSAGE_MAX_CHARS = 5000;
const DEFAULT_API_URL = 'http://localhost:3001';

function getApiBaseUrl(): string {
  const value = import.meta.env.VITE_SYNCANIX_API_URL as string | undefined;
  return typeof value === 'string' && value.length > 0 ? value : DEFAULT_API_URL;
}

type Mode =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'fallback'; mailto: string };

function buildFallbackMailto(topic: ContactTopic, name: string, message: string): string {
  const subject = encodeURIComponent(`[${topic}] ${name}`.slice(0, 150));
  const body = encodeURIComponent(message.slice(0, 1500));
  return `mailto:${CONTACT_ADDRESSES[topic]}?subject=${subject}&body=${body}`;
}

export function ContactForm(): ReactElement {
  const { t, locale } = useT();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [topic, setTopic] = useState<ContactTopic>('sales');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [mode, setMode] = useState<Mode>({ kind: 'idle' });

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (honeypot !== '') {
      // Bot filled the trap: report success, send nothing.
      setMode({ kind: 'success' });
      return;
    }
    setMode({ kind: 'submitting' });
    try {
      const response = await fetch(`${getApiBaseUrl()}/v1/contact`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name, email, company, topic, message, locale }),
      });
      if (response.status === 202) {
        setMode({ kind: 'success' });
        setName('');
        setEmail('');
        setCompany('');
        setMessage('');
        return;
      }
      setMode({ kind: 'fallback', mailto: buildFallbackMailto(topic, name, message) });
    } catch {
      setMode({ kind: 'fallback', mailto: buildFallbackMailto(topic, name, message) });
    }
  }

  return (
    <form
      aria-label={t('contact.form.ariaLabel')}
      className="space-y-6"
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      <div>
        <label htmlFor="contact-name" className="mb-1 block text-sm font-semibold">
          {t('contact.form.nameLabel')}
        </label>
        <input
          id="contact-name"
          type="text"
          required
          maxLength={200}
          autoComplete="name"
          className={fieldClasses()}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
      </div>

      <div>
        <label htmlFor="contact-email" className="mb-1 block text-sm font-semibold">
          {t('contact.form.emailLabel')}
        </label>
        <input
          id="contact-email"
          type="email"
          required
          maxLength={320}
          autoComplete="email"
          className={fieldClasses()}
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
        <p className="mt-1 text-xs text-[color:var(--bd-muted)]">{t('contact.form.emailHelper')}</p>
      </div>

      <div>
        <label htmlFor="contact-company" className="mb-1 block text-sm font-semibold">
          {t('contact.form.companyLabel')}
        </label>
        <input
          id="contact-company"
          type="text"
          maxLength={200}
          autoComplete="organization"
          className={fieldClasses()}
          value={company}
          onChange={(event) => {
            setCompany(event.target.value);
          }}
        />
      </div>

      {/* Four selectable TILES rather than a stack of native radios.
          The radios are still the control — each tile is a `<label>` wrapping
          its own `<input type="radio">`, which keeps the whole native
          behaviour (arrow-key roving, form submission, `:checked`, screen-reader
          announcement) and needs no `role`, no `tabIndex` and no key handler of
          our own. Only the input's PRESENTATION is replaced, by `sr-only` plus
          `peer-checked:` on the tile.

          Worth the change because a native radio stack is the one control on
          this site that still looked like an unstyled 1998 form, sitting in the
          middle of a page whose every other surface is a designed panel — and
          because the four options route to different places, which a tile can
          say and a bare label cannot. */}
      <fieldset>
        <legend className="mb-2 text-sm font-semibold">{t('contact.form.topicLegend')}</legend>
        <div className="grid grid-cols-2 gap-2.5">
          {TOPICS.map((option) => (
            <label
              key={option.value}
              className="group/topic relative flex min-h-11 cursor-pointer items-center gap-2.5 rounded-xl border border-[color:var(--bd-line-2)] bg-[color:var(--bd-page)] px-3.5 py-3 text-sm font-medium text-[color:var(--bd-body)] transition-colors
                hover:border-[color:var(--bd-dot)] hover:bg-[color:var(--bd-sunk)]
                has-[:checked]:border-[color:var(--bd-blue)] has-[:checked]:bg-[color:var(--bd-tint)] has-[:checked]:text-[color:var(--bd-ink)]
                has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-[color:var(--bd-blue)]"
            >
              <input
                type="radio"
                name="contact-topic"
                value={option.value}
                checked={topic === option.value}
                onChange={() => {
                  setTopic(option.value);
                }}
                className="sr-only"
              />
              <span
                aria-hidden="true"
                className="grid size-4 flex-none place-items-center rounded-full border border-[color:var(--bd-dot)] group-has-[:checked]/topic:border-[color:var(--bd-blue)]"
              >
                <span className="size-2 rounded-full bg-transparent group-has-[:checked]/topic:bg-[color:var(--bd-blue)]" />
              </span>
              {t(option.labelKey)}
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <label htmlFor="contact-message" className="mb-1 block text-sm font-semibold">
          {t('contact.form.messageLabel')}
        </label>
        <textarea
          id="contact-message"
          required
          rows={6}
          maxLength={MESSAGE_MAX_CHARS}
          className={fieldClasses()}
          value={message}
          onChange={(event) => {
            setMessage(event.target.value);
          }}
        />
        <p className="mt-1 text-xs text-[color:var(--bd-muted)]">
          {t('contact.form.messageHelper', { max: formatLocaleNumber(MESSAGE_MAX_CHARS, locale) })}
        </p>
      </div>

      {/* Honeypot — humans never see or tab to this. Hidden via the RTL-safe
          `sr-only` clip (NOT an off-screen `-left-[9999px]`, which is a physical
          left that balloons the page width in RTL — perfection-sweep I4). */}
      <div aria-hidden="true" className="sr-only">
        <label htmlFor="contact-website">{t('contact.form.honeypotLabel')}</label>
        {/* eslint-disable-next-line jsx-a11y/autocomplete-valid -- honeypot: the
            unrecognised `sx-no-autofill` token is the DELIBERATE autofill
            suppressor (ui-design §12); a "valid" token would let the trap be
            auto-filled and silently drop a legit user's message. */}
        <input
          id="contact-website"
          type="text"
          name="website"
          tabIndex={-1}
          // `off` is ignored by Chrome (ui-design §12) — an unrecognised token is
          // the reliable autofill suppressor, so the trap can't be auto-filled for
          // a legit user (which would silently drop their message).
          autoComplete="sx-no-autofill"
          value={honeypot}
          onChange={(event) => {
            setHoneypot(event.target.value);
          }}
        />
      </div>

      <p className="text-sm text-[color:var(--bd-muted)]">{t('contact.form.sla')}</p>

      <Cta type="submit" size="md" disabled={mode.kind === 'submitting'}>
        {mode.kind === 'submitting' ? t('contact.form.submitting') : t('contact.form.submit')}
      </Cta>

      {mode.kind === 'success' && (
        <div
          role="status"
          aria-live="polite"
          className="rounded border border-success/30 bg-success/10 p-4 text-sm text-success-foreground"
        >
          <p className="mb-1 font-semibold">{t('contact.form.success.heading')}</p>
          <p>{t('contact.form.success.body')}</p>
        </div>
      )}

      {mode.kind === 'fallback' && (
        <div
          role="alert"
          aria-live="assertive"
          className="rounded border border-danger/30 bg-danger/10 p-4 text-sm text-danger-foreground"
        >
          <p className="mb-1 font-semibold">{t('contact.form.fallback.heading')}</p>
          <p>
            {t('contact.form.fallback.body')}{' '}
            <a className={INLINE_LINK} href={mode.mailto}>
              {CONTACT_ADDRESSES[topic]}
            </a>
          </p>
        </div>
      )}
    </form>
  );
}
